import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, FileText, Receipt, Inbox, LayoutDashboard, Bot, Activity, Landmark } from "lucide-react";
import { AccountMenu } from "./AccountMenu";
import { UploadButton } from "./UploadButton";
import { StatementBadge } from "./Badges";
import { fmtDate } from "../lib/format";
import { APP_VERSION, UI_HASH } from "../version";
import { api } from "../lib/api";

const VersionInfo = () => {
  const [h, setH] = useState(null);
  useEffect(() => { api.health().then(setH).catch(() => {}); }, []);
  const stale = h?.ui_hash && UI_HASH && h.ui_hash !== UI_HASH;
  return (
    <div className="mt-2 px-1 text-[10px] leading-tight text-slate-400" data-testid="app-version">
      App {APP_VERSION}{h && <> · Server {h.version}{h.renderer ? ` · PDF: ${h.renderer === "chromium" ? "Chromium" : "Ersatz"}` : ""}</>}
      {stale && <div className="text-amber-600" data-testid="app-version-stale">Neue Version verfügbar – bitte Seite neu laden</div>}
    </div>
  );
};

const groupByYear = (statements) => {
  const map = {};
  statements.forEach((s) => {
    const y = s.statement_year || "Ohne Jahr";
    (map[y] = map[y] || []).push(s);
  });
  return Object.entries(map).sort((a, b) => String(b[0]).localeCompare(String(a[0])));
};

const StatementItem = ({ st, active, onSelect }) => (
  <button
    data-testid={`statement-tree-item-${st.id}`}
    onClick={() => onSelect(st.id)}
    className={`group flex w-full items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors duration-150 ${
      active ? "border-slate-900 bg-white shadow-sm" : "border-transparent hover:border-slate-200 hover:bg-white/70"
    }`}
  >
    <FileText className={`mt-0.5 h-4 w-4 shrink-0 ${active ? "text-slate-900" : "text-slate-400"}`} />
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <span className="break-all text-[15px] font-semibold text-slate-900">
          {st.virtual ? "Laufende Buchungen" : st.statement_number ? `Auszug ${st.statement_number}` : st.file?.original_filename}
        </span>
        {st.virtual ? <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800" data-testid={`statement-badge-${st.id}`}>Bank</span> : <StatementBadge st={st} testId={`statement-badge-${st.id}`} />}
      </div>
      <div className="mt-0.5 text-xs text-slate-500">
        {st.period_from ? `${fmtDate(st.period_from)} – ${fmtDate(st.period_to)}` : "Zeitraum wird ermittelt"}
        {st.balance_check?.transaction_count ? ` · ${st.balance_check.transaction_count} Buchungen` : ""}
      </div>
      {st.status === "ready" && <MiniProgress done={st.progress?.done || 0} total={st.progress?.total || 0} testId={`statement-progress-${st.id}`} />}
    </div>
  </button>
);

const MiniProgress = ({ done, total, testId }) => {
  const pct = total ? (done / total) * 100 : 0;
  return (
    <div className="mt-1.5" data-testid={testId}>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-200">
        <div className="h-full rounded-full bg-emerald-600 transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="font-num mt-0.5 text-[11px] text-slate-500"><span data-testid={`${testId}-text`}>{done}/{total}</span> erledigt</div>
    </div>
  );
};

const NavButton = ({ active, onClick, icon: Icon, label, badge, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[15px] font-medium transition-colors duration-150 ${active ? "bg-white text-slate-900 shadow-sm" : "text-slate-700 hover:bg-white/70"}`}>
    <Icon className="h-4 w-4 text-slate-500" />{label}
    {badge > 0 && <span className="font-num ml-auto rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white" data-testid={`${testId}-badge`}>{badge}</span>}
  </button>
);

const YearGroup = ({ year, items, selectedId, onSelect }) => {
  const [open, setOpen] = useState(true);
  return (
    <div data-testid={`year-group-${year}`}>
      <button
        data-testid={`year-toggle-${year}`}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-1.5 px-1 py-1.5 text-sm font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <span className="font-display">{year}</span>
        <span className="ml-auto text-xs font-medium normal-case tracking-normal text-slate-400">{items.length} Auszug{items.length !== 1 ? "e" : ""}</span>
      </button>
      <div className="px-1"><MiniProgress done={items.reduce((a, s) => a + (s.progress?.done || 0), 0)} total={items.reduce((a, s) => a + (s.progress?.total || 0), 0)} testId={`year-progress-${year}`} /></div>
      {open && (
        <div className="mt-1 space-y-1">
          {items.map((st) => <StatementItem key={st.id} st={st} active={st.id === selectedId} onSelect={onSelect} />)}
        </div>
      )}
    </div>
  );
};

export const Sidebar = ({ statements, selectedId, onSelect, onUploaded, view, onView, inboxCount, user, onLogout, inSheet }) => (
  <aside data-testid="sidebar" className={`flex shrink-0 flex-col bg-[#F3F2EE] ${inSheet ? "h-full w-full overflow-y-auto" : "thin-scroll h-screen w-[260px] overflow-y-auto border-r border-slate-200 min-[1440px]:w-72"}`}>
    <div className="flex items-center gap-2.5 px-5 pb-4 pt-5">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-amber-300">
        <Receipt className="h-5 w-5" />
      </div>
      <div>
        <div className="font-display text-xl font-extrabold tracking-tight text-slate-900" data-testid="app-title">BelegFinder</div>
        <div className="text-xs text-slate-500">Jeder Buchung ihr Beleg</div>
      </div>
    </div>
    <div className="px-4 pb-4">
      <UploadButton onUploaded={onUploaded} onDuplicate={onSelect} />
    </div>
    <nav className="space-y-0.5 px-3 pb-3">
      <NavButton icon={LayoutDashboard} label="Übersicht" active={view === "overview"} onClick={() => onView("overview")} testId="nav-overview" />
      <NavButton icon={Inbox} label="Belege-Eingang" badge={inboxCount} active={view === "inbox"} onClick={() => onView("inbox")} testId="nav-inbox" />
      <NavButton icon={Bot} label="Agent" active={view === "agent"} onClick={() => onView("agent")} testId="nav-agent" />
      <NavButton icon={Activity} label="Agent-Aktivität" active={view === "activity"} onClick={() => onView("activity")} testId="nav-activity" />
      <NavButton icon={Landmark} label="Bankverbindung" active={view === "banking"} onClick={() => onView("banking")} testId="nav-banking" />
    </nav>
    <div className="thin-scroll max-h-72 flex-1 space-y-4 overflow-y-auto px-3 pb-5 lg:max-h-none">
      {statements.length === 0 ? (
        <p className="px-2 text-sm leading-relaxed text-slate-500" data-testid="sidebar-empty">
          Noch keine Kontoauszüge. Laden Sie Ihren ersten Erste-Bank-Auszug (PDF) hoch.
        </p>
      ) : (
        groupByYear(statements).map(([year, items]) => (
          <YearGroup key={year} year={year} items={items} selectedId={selectedId} onSelect={onSelect} />
        ))
      )}
    </div>
    <div className="px-3 pb-4"><AccountMenu user={user} onLogout={onLogout} /><VersionInfo /></div>
  </aside>
);
